import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Pause, Volume2, VolumeX, RotateCcw, Maximize2, 
  Download, Eye, EyeOff, Layers, Clock, FileVideo, FileAudio 
} from 'lucide-react';
import { MediaFile } from '../types/media';

interface MediaPreviewProps {
  originalFile: MediaFile;
  processedFile: MediaFile | null;
  isProcessing: boolean;
  onResetPreview: () => void;
}

const MediaPreview: React.FC<MediaPreviewProps> = ({
  originalFile,
  processedFile,
  isProcessing,
  onResetPreview
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showComparison, setShowComparison] = useState(false);
  const [activePreview, setActivePreview] = useState<'original' | 'processed'>('original');
  
  const originalRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const processedRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);

  useEffect(() => {
    const media = originalRef.current;
    if (media) {
      const updateTime = () => setCurrentTime(media.currentTime);
      const updateDuration = () => setDuration(media.duration);
      
      media.addEventListener('timeupdate', updateTime);
      media.addEventListener('loadedmetadata', updateDuration);
      media.addEventListener('ended', () => setIsPlaying(false));
      
      return () => {
        media.removeEventListener('timeupdate', updateTime);
        media.removeEventListener('loadedmetadata', updateDuration);
        media.removeEventListener('ended', () => setIsPlaying(false));
      };
    }
  }, [originalFile]);

  const togglePlayPause = () => {
    const media = activePreview === 'original' ? originalRef.current : processedRef.current;
    if (media) {
      if (isPlaying) {
        media.pause();
      } else {
        media.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    const media = activePreview === 'original' ? originalRef.current : processedRef.current;
    if (media) {
      media.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const media = activePreview === 'original' ? originalRef.current : processedRef.current;
    if (media) {
      const time = parseFloat(e.target.value);
      media.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const downloadFile = (file: MediaFile) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-black">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
              {originalFile.type === 'video' ? (
                <FileVideo className="w-4 h-4" />
              ) : (
                <FileAudio className="w-4 h-4" />
              )}
            </div>
            <div>
              <h3 className="font-semibold">{originalFile.name}</h3>
              <p className="text-sm text-gray-400">
                {originalFile.type === 'video' 
                  ? `${originalFile.metadata.width}×${originalFile.metadata.height}` 
                  : 'Audio File'
                } • {formatTime(originalFile.duration)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {processedFile && (
              <>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowComparison(!showComparison)}
                  className={`p-2 rounded-lg transition-colors ${
                    showComparison ? 'bg-blue-600 text-white' : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                  title="Toggle comparison view"
                >
                  <Layers className="w-4 h-4" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onResetPreview}
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Reset to original"
                >
                  <RotateCcw className="w-4 h-4" />
                </motion.button>
              </>
            )}
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              title="Fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Preview Area */}
      <div className="relative">
        {isProcessing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10"
          >
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 border-4 border-gray-600 border-t-white rounded-full mx-auto mb-4"
              />
              <p className="text-white font-medium">Processing your media...</p>
              <p className="text-gray-400 text-sm mt-1">This may take a moment</p>
            </div>
          </motion.div>
        )}

        {showComparison && processedFile ? (
          /* Comparison View */
          <div className="grid grid-cols-2 gap-1">
            <div className="relative group">
              <div className="absolute top-2 left-2 bg-black bg-opacity-75 px-2 py-1 rounded text-xs font-medium z-10">
                Original
              </div>
              {originalFile.type === 'video' ? (
                <video
                  ref={originalRef as React.RefObject<HTMLVideoElement>}
                  src={originalFile.url}
                  className="w-full aspect-video bg-black object-contain"
                  onClick={() => setActivePreview('original')}
                />
              ) : (
                <div className="w-full aspect-video bg-black flex items-center justify-center">
                  <audio
                    ref={originalRef as React.RefObject<HTMLAudioElement>}
                    src={originalFile.url}
                    className="hidden"
                  />
                  <div className="text-center">
                    <FileAudio className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400">Original Audio</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="relative group">
              <div className="absolute top-2 left-2 bg-black bg-opacity-75 px-2 py-1 rounded text-xs font-medium z-10">
                Processed
              </div>
              {processedFile.type === 'video' ? (
                <video
                  ref={processedRef as React.RefObject<HTMLVideoElement>}
                  src={processedFile.url}
                  className="w-full aspect-video bg-black object-contain"
                  onClick={() => setActivePreview('processed')}
                />
              ) : (
                <div className="w-full aspect-video bg-black flex items-center justify-center">
                  <audio
                    ref={processedRef as React.RefObject<HTMLAudioElement>}
                    src={processedFile.url}
                    className="hidden"
                  />
                  <div className="text-center">
                    <FileAudio className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <p className="text-green-400">Processed Audio</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Single View */
          <div className="relative">
            {processedFile && !showComparison ? (
              /* Processed File View */
              processedFile.type === 'video' ? (
                <video
                  ref={processedRef as React.RefObject<HTMLVideoElement>}
                  src={processedFile.url}
                  className="w-full aspect-video bg-black object-contain"
                />
              ) : (
                <div className="w-full aspect-video bg-black flex items-center justify-center">
                  <audio
                    ref={processedRef as React.RefObject<HTMLAudioElement>}
                    src={processedFile.url}
                    className="hidden"
                  />
                  <div className="text-center">
                    <FileAudio className="w-24 h-24 text-green-400 mx-auto mb-4" />
                    <p className="text-green-400 text-lg font-medium">Processed Audio</p>
                    <p className="text-gray-400 text-sm mt-2">{processedFile.name}</p>
                  </div>
                </div>
              )
            ) : (
              /* Original File View */
              originalFile.type === 'video' ? (
                <video
                  ref={originalRef as React.RefObject<HTMLVideoElement>}
                  src={originalFile.url}
                  className="w-full aspect-video bg-black object-contain"
                />
              ) : (
                <div className="w-full aspect-video bg-black flex items-center justify-center">
                  <audio
                    ref={originalRef as React.RefObject<HTMLAudioElement>}
                    src={originalFile.url}
                    className="hidden"
                  />
                  <div className="text-center">
                    <FileAudio className="w-24 h-24 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg font-medium">Audio File</p>
                    <p className="text-gray-500 text-sm mt-2">{originalFile.name}</p>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-4 bg-black border-t border-gray-800">
        {/* Timeline */}
        <div className="mb-4">
          <div className="flex items-center space-x-3 text-sm text-gray-400 mb-2">
            <Clock className="w-4 h-4" />
            <span>{formatTime(currentTime)}</span>
            <span>/</span>
            <span>{formatTime(duration)}</span>
          </div>
          
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
          />
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={togglePlayPause}
              className="w-10 h-10 bg-white text-black rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleMute}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </motion.button>
          </div>

          <div className="flex items-center space-x-2">
            {processedFile && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => downloadFile(processedFile)}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="text-sm font-medium">Download Processed</span>
              </motion.button>
            )}
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => downloadFile(originalFile)}
              className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm font-medium">Download Original</span>
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaPreview;